import React, { useState } from "react";
import { TextField, Box, IconButton, Tooltip, InputAdornment } from "@material-ui/core";
import { Language } from "@material-ui/icons";
import { css } from "@emotion/css";
import { LocalizedText, TranslationLocale, PRIMARY_LOCALE, TRANSLATION_LOCALES } from "./locales";
import { sanitizeQuotes } from "./sanitize";

type TranslatedTextFieldProps = {
  label: string;
  value: LocalizedText;
  onChange: (value: LocalizedText) => void;
  multiline?: boolean;
  rows?: number;
  disabled?: boolean;
};

const TRANSLATION_KEYS = Object.keys(TRANSLATION_LOCALES) as TranslationLocale[];

const TranslatedTextField: React.VFC<TranslatedTextFieldProps> = ({
  label,
  value,
  onChange,
  multiline = false,
  rows = 1,
  disabled = false,
}) => {
  const [expanded, setExpanded] = useState(false);

  const handlePrimaryChange = (text: string) => {
    onChange({ ...value, "en-us": text });
  };

  const handlePrimaryPaste: React.ClipboardEventHandler = e => {
    const pasted = e.clipboardData.getData("text");
    const sanitized = sanitizeQuotes(pasted);
    if (sanitized !== pasted) {
      e.preventDefault();
      const input = e.target as HTMLInputElement;
      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? 0;
      const current = value["en-us"] ?? "";
      handlePrimaryChange(current.slice(0, start) + sanitized + current.slice(end));
    }
  };

  const handlePrimaryBlur = () => {
    const current = value["en-us"] ?? "";
    const sanitized = sanitizeQuotes(current);
    if (sanitized !== current) {
      handlePrimaryChange(sanitized);
    }
  };

  const handleTranslationChange = (locale: TranslationLocale, text: string) => {
    const updated = { ...value };
    if (text) {
      updated[locale] = text;
    } else {
      delete updated[locale];
    }
    onChange(updated);
  };

  const handleTranslationPaste =
    (locale: TranslationLocale): React.ClipboardEventHandler =>
    e => {
      const pasted = e.clipboardData.getData("text");
      const sanitized = sanitizeQuotes(pasted);
      if (sanitized !== pasted) {
        e.preventDefault();
        const input = e.target as HTMLInputElement;
        const start = input.selectionStart ?? 0;
        const end = input.selectionEnd ?? 0;
        const current = value[locale] ?? "";
        handleTranslationChange(locale, current.slice(0, start) + sanitized + current.slice(end));
      }
    };

  const handleTranslationBlur = (locale: TranslationLocale) => () => {
    const current = value[locale] ?? "";
    const sanitized = sanitizeQuotes(current);
    if (sanitized !== current) {
      handleTranslationChange(locale, sanitized);
    }
  };

  const hasTranslations = TRANSLATION_KEYS.some(locale => value[locale]);

  return (
    <Box className={styles.container}>
      <TextField
        label={label}
        value={value["en-us"] ?? ""}
        onChange={e => handlePrimaryChange(e.target.value)}
        onPaste={handlePrimaryPaste}
        onBlur={handlePrimaryBlur}
        fullWidth
        multiline={multiline}
        rows={rows}
        variant="outlined"
        margin="normal"
        disabled={disabled}
        InputProps={{
          startAdornment: expanded ? (
            <InputAdornment position="start">
              <span className={styles.flag}>{PRIMARY_LOCALE.flag}</span>
            </InputAdornment>
          ) : undefined,
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title={expanded ? "Hide translations" : "Show translations"}>
                <IconButton
                  size="small"
                  onClick={() => setExpanded(!expanded)}
                  className={hasTranslations ? styles.hasTranslations : undefined}
                >
                  <Language fontSize="small" />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
      />
      {expanded && (
        <Box className={styles.translations}>
          {TRANSLATION_KEYS.map(locale => {
            const config = TRANSLATION_LOCALES[locale];
            return (
              <TextField
                key={locale}
                value={value[locale] ?? ""}
                onChange={e => handleTranslationChange(locale, e.target.value)}
                onPaste={handleTranslationPaste(locale)}
                onBlur={handleTranslationBlur(locale)}
                fullWidth
                multiline={multiline}
                rows={rows}
                variant="outlined"
                size="small"
                disabled={disabled}
                placeholder={config.label}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <span className={styles.flag}>{config.flag}</span>
                    </InputAdornment>
                  ),
                }}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const styles = {
  container: css``,
  translations: css`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
    margin-bottom: 16px;
    padding-left: 16px;
    border-left: 2px solid #e0e0e0;
  `,
  flag: css`
    font-size: 16px;
  `,
  hasTranslations: css`
    color: #1976d2;
  `,
};

export default TranslatedTextField;
